import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import { ko, enUS } from 'date-fns/locale';
import { fetchLogs } from '../api/logs';
import { fetchExploitRules, addGlobalExploitExclusion, addHostExploitExclusion, fetchExploitRuleHosts } from '../api/exploit-rules';
import { banIP } from '../api/security';
import { api } from '../api/client';
import type { Log, LogFilter } from '../types/log';
import type { ExploitRulesResponse, ExploitBlockRuleCategory } from '../types/exploit-rules';
import { HelpTip } from './common/HelpTip';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { useDebounce } from './log-viewer/utils';

function formatTime(timestamp: string, language: string = 'ko'): string {
  return new Date(timestamp).toLocaleString(language);
}

function StatusCodeBadge({ code }: { code: number }) {
  let color = 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
  if (code >= 200 && code < 300) color = 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
  else if (code >= 300 && code < 400) color = 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
  else if (code >= 400 && code < 500) color = 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
  else if (code >= 500) color = 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${color}`}>
      {code}
    </span>
  );
}

function ExploitRuleBadge({ ruleId, ruleName }: { ruleId?: string; ruleName?: string }) {
  if (!ruleId) return <span className="text-slate-400">-</span>;

  // Determine color and icon based on rule name or category
  let color = 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300';
  let icon = '🛡️';
  const displayName = ruleName || ruleId;
  const lowerName = displayName.toLowerCase();

  if (lowerName.includes('sql') || lowerName.includes('injection')) {
    color = 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300';
    icon = '💉';
  } else if (lowerName.includes('rfi') || lowerName.includes('lfi') || lowerName.includes('file') || lowerName.includes('traversal')) {
    color = 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300';
    icon = '📂';
  } else if (lowerName.includes('scan') || lowerName.includes('probe')) {
    color = 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300';
    icon = '🔍';
  } else if (lowerName.includes('method') || lowerName.includes('http')) {
    color = 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300';
    icon = '📡';
  } else if (lowerName.includes('vcs') || lowerName.includes('git') || lowerName.includes('.env')) {
    color = 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300';
    icon = '📁';
  } else if (lowerName.includes('xss') || lowerName.includes('script')) {
    color = 'bg-pink-100 dark:bg-pink-900/30 text-pink-800 dark:text-pink-300';
    icon = '⚠️';
  }

  // Show rule name if available, otherwise truncated ID
  const label = ruleName || `${ruleId.substring(0, 8)}...`;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${color}`} title={displayName}>
      <span>{icon}</span>
      <span className="max-w-[150px] truncate">{label}</span>
    </span>
  );
}

interface LogDetailModalProps {
  log: Log;
  ruleName?: string;
  onClose: () => void;
  onExceptionAdded?: () => void;
}

function LogDetailModal({ log, ruleName, onClose, onExceptionAdded }: LogDetailModalProps) {
  const { t, i18n } = useTranslation('exploitLogs');
  const queryClient = useQueryClient();
  const [showExceptionForm, setShowExceptionForm] = useState(false);
  const [exceptionType, setExceptionType] = useState<'global' | 'host'>('global');
  const [selectedHostId, setSelectedHostId] = useState<string>('');
  const [exceptionReason, setExceptionReason] = useState('');
  const [exceptionMessage, setExceptionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // IP Ban states
  const [showBanForm, setShowBanForm] = useState(false);
  const [banReason, setBanReason] = useState('');
  const [banDuration, setBanDuration] = useState<number | undefined>(undefined);
  const [isGlobalBan, setIsGlobalBan] = useState(false);
  const [proxyHostId, setProxyHostId] = useState<string | null>(null);

  useEscapeKey(onClose);

  // Fetch proxy host ID for the current host (for per-host bans)
  const { data: hostData } = useQuery({
    queryKey: ['proxyHostByDomain', log.host],
    queryFn: async () => {
      if (!log.host) return null;
      const response = await api.get<{ id: string }>(`/api/v1/proxy-hosts/by-domain/${encodeURIComponent(log.host)}`);
      return response.id || null;
    },
    enabled: !!log.host && showBanForm,
    staleTime: 60000,
  });

  // Update proxyHostId when data is fetched
  useEffect(() => {
    if (hostData) {
      setProxyHostId(hostData);
    }
  }, [hostData]);

  // Fetch hosts for host-specific exceptions
  const { data: hostsData } = useQuery({
    queryKey: ['exploit-rule-hosts'],
    queryFn: fetchExploitRuleHosts,
    enabled: showExceptionForm,
  });

  const globalExclusionMutation = useMutation({
    mutationFn: () => addGlobalExploitExclusion(log.exploit_rule!, exceptionReason || `Excluded from log: ${log.request_uri}`),
    onSuccess: () => {
      setExceptionMessage({ type: 'success', text: t('modal.exception.globalSuccess') });
      queryClient.invalidateQueries({ queryKey: ['exploit-rules'] });
      setShowExceptionForm(false);
      onExceptionAdded?.();
    },
    onError: (error: Error) => {
      setExceptionMessage({ type: 'error', text: error.message });
    },
  });

  const hostExclusionMutation = useMutation({
    mutationFn: () => addHostExploitExclusion(selectedHostId, log.exploit_rule!, exceptionReason || `Excluded from log: ${log.request_uri}`),
    onSuccess: () => {
      setExceptionMessage({ type: 'success', text: t('modal.exception.hostSuccess') });
      queryClient.invalidateQueries({ queryKey: ['exploit-rules'] });
      setShowExceptionForm(false);
      onExceptionAdded?.();
    },
    onError: (error: Error) => {
      setExceptionMessage({ type: 'error', text: error.message });
    },
  });

  // IP Ban mutation
  const banMutation = useMutation({
    mutationFn: () => {
      if (!log.client_ip) {
        throw new Error('Missing client IP');
      }
      const banData: { ip_address: string; reason?: string; ban_time?: number; proxy_host_id?: string } = {
        ip_address: log.client_ip,
        reason: banReason || `Blocked from exploit log - ${log.exploit_rule || 'N/A'}: ${log.request_uri || 'N/A'}`,
        ban_time: banDuration,
      };
      if (!isGlobalBan && proxyHostId) {
        banData.proxy_host_id = proxyHostId;
      }
      return banIP(banData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['banned-ips'] });
      alert(isGlobalBan
        ? t('banIP.successGlobal', { ip: log.client_ip, defaultValue: '{{ip}}가 전역 차단되었습니다.' })
        : t('banIP.success', { ip: log.client_ip, defaultValue: '{{ip}}가 차단되었습니다.' }));
      setShowBanForm(false);
      setBanReason('');
      setBanDuration(undefined);
      setIsGlobalBan(false);
    },
    onError: (error) => {
      alert(t('banIP.failed', { error: (error as Error).message, defaultValue: 'IP 차단 실패: {{error}}' }));
    },
  });

  const handleAddException = () => {
    if (exceptionType === 'global') {
      globalExclusionMutation.mutate();
    } else if (selectedHostId) {
      hostExclusionMutation.mutate();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between bg-red-50 dark:bg-red-900/20">
          <h3 className="text-lg font-semibold text-red-900 dark:text-red-100">{t('modal.title')}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)] space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-500 dark:text-slate-400 font-medium">{t('modal.timestamp')}</label>
              <p className="text-sm text-slate-900 dark:text-white">{new Date(log.timestamp).toLocaleString(i18n.language)}</p>
            </div>
            <div>
              <label className="text-xs text-slate-500 dark:text-slate-400 font-medium">{t('modal.exploitRule')}</label>
              <p className="mt-1">
                <ExploitRuleBadge ruleId={log.exploit_rule} ruleName={ruleName} />
              </p>
            </div>
            <div>
              <label className="text-xs text-slate-500 dark:text-slate-400 font-medium">{t('modal.clientIp')}</label>
              <div className="flex items-center gap-2">
                {log.client_ip ? (
                  <button
                    onClick={() => setShowBanForm(!showBanForm)}
                    className="group flex items-center gap-2 px-2 py-1 -ml-2 text-sm font-mono text-slate-900 dark:text-white hover:bg-red-50 dark:hover:bg-red-900/30 hover:text-red-700 dark:hover:text-red-400 rounded-lg transition-colors"
                    title={t('banIP.clickToBan', { defaultValue: 'IP 차단하려면 클릭' })}
                  >
                    <span className={log.is_banned ? 'text-red-600 dark:text-red-400' : ''}>{log.client_ip}</span>
                    {log.is_banned && (
                      <span className="px-1.5 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs font-medium rounded">
                        Banned
                      </span>
                    )}
                    <svg className="w-4 h-4 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                    </svg>
                  </button>
                ) : (
                  <p className="text-sm text-slate-900 dark:text-white font-mono">-</p>
                )}
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-500 dark:text-slate-400 font-medium">{t('modal.statusCode')}</label>
              <p className="mt-1">{log.status_code && <StatusCodeBadge code={log.status_code} />}</p>
            </div>
            <div>
              <label className="text-xs text-slate-500 dark:text-slate-400 font-medium">{t('modal.host')}</label>
              <p className="text-sm text-slate-900 dark:text-white">{log.host || '-'}</p>
            </div>
            <div>
              <label className="text-xs text-slate-500 dark:text-slate-400 font-medium">{t('modal.method')}</label>
              <p className="text-sm text-slate-900 dark:text-white font-mono">{log.request_method || '-'}</p>
            </div>
            <div>
              <label className="text-xs text-slate-500 dark:text-slate-400 font-medium">{t('modal.country')}</label>
              <p className="text-sm text-slate-900 dark:text-white">{log.geo_country_code || '-'}</p>
            </div>
          </div>

          {/* IP Ban Form */}
          {showBanForm && log.client_ip && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <h4 className="text-sm font-semibold text-red-800 dark:text-red-300 mb-3">{t('banIP.title', { ip: log.client_ip, defaultValue: '{{ip}} 차단' })}</h4>
              <div className="space-y-3">
                {/* Scope Toggle */}
                <div>
                  <label className="text-xs font-medium text-red-700 dark:text-red-400 uppercase mb-2 block">
                    {t('banIP.scope', { defaultValue: '적용 범위' })}
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setIsGlobalBan(false)}
                      className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                        !isGlobalBan
                          ? 'bg-red-600 text-white'
                          : 'bg-white dark:bg-slate-800 text-red-700 dark:text-red-400 border border-red-300 dark:border-red-700 hover:bg-red-50 dark:hover:bg-red-900/30'
                      }`}
                    >
                      {t('banIP.perHost', { defaultValue: '이 호스트만', host: log.host })}
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsGlobalBan(true)}
                      className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                        isGlobalBan
                          ? 'bg-purple-600 text-white'
                          : 'bg-white dark:bg-slate-800 text-purple-700 dark:text-purple-400 border border-purple-300 dark:border-purple-700 hover:bg-purple-50 dark:hover:bg-purple-900/30'
                      }`}
                    >
                      {t('banIP.global', { defaultValue: '전역 차단 (모든 호스트)' })}
                    </button>
                  </div>
                  {isGlobalBan && (
                    <p className="mt-1 text-xs text-purple-600 dark:text-purple-400">
                      {t('banIP.globalDescription', { defaultValue: '이 IP는 모든 프록시 호스트에서 차단됩니다.' })}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-xs font-medium text-red-700 dark:text-red-400 uppercase mb-1 block">
                    {t('banIP.reason', { defaultValue: '차단 사유' })}
                  </label>
                  <input
                    type="text"
                    value={banReason}
                    onChange={(e) => setBanReason(e.target.value)}
                    placeholder={t('banIP.reasonPlaceholder', { defaultValue: '차단 사유를 입력하세요 (선택)' })}
                    className="w-full px-3 py-2 border border-red-300 dark:border-red-700 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-red-700 dark:text-red-400 uppercase mb-1 block">
                    {t('banIP.duration', { defaultValue: '차단 기간' })}
                  </label>
                  <select
                    value={banDuration ?? ''}
                    onChange={(e) => setBanDuration(e.target.value ? parseInt(e.target.value) : undefined)}
                    className="w-full px-3 py-2 border border-red-300 dark:border-red-700 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
                  >
                    <option value="">{t('banIP.permanent', { defaultValue: '영구 차단' })}</option>
                    <option value="3600">{t('banIP.duration1h', { defaultValue: '1시간' })}</option>
                    <option value="86400">{t('banIP.duration1d', { defaultValue: '1일' })}</option>
                    <option value="604800">{t('banIP.duration1w', { defaultValue: '1주일' })}</option>
                    <option value="2592000">{t('banIP.duration1m', { defaultValue: '1개월' })}</option>
                  </select>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => banMutation.mutate()}
                    disabled={banMutation.isPending}
                    className={`flex-1 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 ${
                      isGlobalBan ? 'bg-purple-600 hover:bg-purple-700' : 'bg-red-600 hover:bg-red-700'
                    }`}
                  >
                    {banMutation.isPending ? t('banIP.processing', { defaultValue: '처리 중...' }) : (isGlobalBan ? t('banIP.submitGlobal', { defaultValue: '전역 차단' }) : t('banIP.submit', { defaultValue: '차단' }))}
                  </button>
                  <button
                    onClick={() => {
                      setShowBanForm(false);
                      setBanReason('');
                      setBanDuration(undefined);
                      setIsGlobalBan(false);
                    }}
                    className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition-colors"
                  >
                    {t('banIP.cancel', { defaultValue: '취소' })}
                  </button>
                </div>
              </div>
            </div>
          )}

          {ruleName && (
            <div>
              <label className="text-xs text-slate-500 dark:text-slate-400 font-medium">{t('modal.ruleName')}</label>
              <p className="text-sm text-slate-900 dark:text-white bg-red-50 dark:bg-red-900/30 p-2 rounded">{ruleName}</p>
            </div>
          )}

          <div>
            <label className="text-xs text-slate-500 dark:text-slate-400 font-medium">{t('modal.requestUri')}</label>
            <p className="text-sm text-slate-900 dark:text-white font-mono break-all bg-slate-50 dark:bg-slate-700 p-2 rounded">{log.request_uri || '-'}</p>
          </div>

          <div>
            <label className="text-xs text-slate-500 dark:text-slate-400 font-medium">{t('modal.userAgent')}</label>
            <p className="text-sm text-slate-900 dark:text-white font-mono break-all bg-slate-50 dark:bg-slate-700 p-2 rounded">{log.http_user_agent || '-'}</p>
          </div>

          {log.raw_log && (
            <div>
              <label className="text-xs text-slate-500 dark:text-slate-400 font-medium">{t('modal.rawLog')}</label>
              <pre className="text-xs text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 p-3 rounded overflow-x-auto whitespace-pre-wrap break-all">{log.raw_log}</pre>
            </div>
          )}

          {/* Exception Message */}
          {exceptionMessage && (
            <div className={`px-4 py-3 rounded-lg text-sm font-medium ${exceptionMessage.type === 'success'
              ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800'
              : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800'
              }`}>
              {exceptionMessage.text}
            </div>
          )}

          {/* Exception Form */}
          {log.exploit_rule && (
            <div className="border-t border-slate-200 dark:border-slate-700 pt-4 mt-4">
              {!showExceptionForm ? (
                <button
                  onClick={() => setShowExceptionForm(true)}
                  className="w-full px-4 py-2 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 rounded-lg hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {t('modal.exception.addButton')}
                </button>
              ) : (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 space-y-3">
                  <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-100">{t('modal.exception.title')}</h4>

                  {/* Exception Type Selection */}
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="exceptionType"
                        value="global"
                        checked={exceptionType === 'global'}
                        onChange={() => setExceptionType('global')}
                        className="w-4 h-4 text-amber-600"
                      />
                      <span className="text-sm text-slate-700 dark:text-slate-300">{t('modal.exception.global')}</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="exceptionType"
                        value="host"
                        checked={exceptionType === 'host'}
                        onChange={() => setExceptionType('host')}
                        className="w-4 h-4 text-amber-600"
                      />
                      <span className="text-sm text-slate-700 dark:text-slate-300">{t('modal.exception.perHost')}</span>
                    </label>
                  </div>

                  {/* Host Selection (only for per-host exception) */}
                  {exceptionType === 'host' && (
                    <div>
                      <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">{t('modal.exception.selectHost')}</label>
                      <select
                        value={selectedHostId}
                        onChange={(e) => setSelectedHostId(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                      >
                        <option value="">{t('modal.exception.selectHostPlaceholder')}</option>
                        {hostsData?.hosts?.map((host) => (
                          <option key={host.proxy_host_id} value={host.proxy_host_id}>
                            {host.proxy_host_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Reason Input */}
                  <div>
                    <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">{t('modal.exception.reason')}</label>
                    <input
                      type="text"
                      value={exceptionReason}
                      onChange={(e) => setExceptionReason(e.target.value)}
                      placeholder={t('modal.exception.reasonPlaceholder')}
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 justify-end">
                    <button
                      onClick={() => setShowExceptionForm(false)}
                      className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
                    >
                      {t('modal.exception.cancel')}
                    </button>
                    <button
                      onClick={handleAddException}
                      disabled={exceptionType === 'host' && !selectedHostId || globalExclusionMutation.isPending || hostExclusionMutation.isPending}
                      className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {globalExclusionMutation.isPending || hostExclusionMutation.isPending ? t('modal.exception.adding') : t('modal.exception.confirm')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function ExploitBlockLogs() {
  const { t, i18n } = useTranslation('exploitLogs');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [selectedLog, setSelectedLog] = useState<Log | null>(null);
  const [ruleFilter, setRuleFilter] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | null>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [endDate, setEndDate] = useState<Date | null>(() => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
  });
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 300);
  const dateLocale = i18n.language === 'ko' ? ko : enUS;

  // Fetch exploit rules for rule name mapping
  const { data: rulesData } = useQuery<ExploitRulesResponse>({
    queryKey: ['exploit-rules'],
    queryFn: fetchExploitRules,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Build rule ID to name mapping
  const ruleNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (rulesData?.categories) {
      rulesData.categories.forEach((cat: ExploitBlockRuleCategory) => {
        cat.rules?.forEach((rule) => {
          map[rule.id] = rule.name;
        });
      });
    }
    return map;
  }, [rulesData]);

  const filter: LogFilter = useMemo(() => ({
    log_type: 'access',
    block_reason: 'exploit_block',
    exploit_rule: ruleFilter || undefined,
    start_time: startDate?.toISOString(),
    end_time: endDate?.toISOString(),
    search: debouncedSearch || undefined,
  }), [ruleFilter, startDate, endDate, debouncedSearch]);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['exploit-block-logs', filter, page, perPage],
    queryFn: () => fetchLogs(page, perPage, filter),
    refetchInterval: 10000,
  });

  // Fetch total stats
  const baseStatsFilter: LogFilter = useMemo(() => ({
    log_type: 'access',
    block_reason: 'exploit_block',
    start_time: startDate?.toISOString(),
    end_time: endDate?.toISOString(),
  }), [startDate, endDate]);

  const { data: totalData } = useQuery({
    queryKey: ['exploit-block-total', baseStatsFilter],
    queryFn: () => fetchLogs(1, 1, baseStatsFilter),
    refetchInterval: 10000,
  });

  // Get unique rules from rulesData for filter dropdown
  const ruleOptions = useMemo(() => {
    const options: { id: string; name: string }[] = [];
    if (rulesData?.categories) {
      rulesData.categories.forEach((cat: ExploitBlockRuleCategory) => {
        cat.rules?.forEach((rule) => {
          options.push({ id: rule.id, name: rule.name });
        });
      });
    }
    return options;
  }, [rulesData]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
            <span className="text-xl">🛡️</span>
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">{t('title')}</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">{t('subtitle')}</p>
          </div>
        </div>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          {t('refresh')}
          <HelpTip content={t('refreshHelp')} />
        </button>
      </div>

      {/* Stats Card */}
      <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
        <div className="flex items-center gap-4">
          <div className="text-3xl font-bold text-red-600 dark:text-red-400">{totalData?.total || 0}</div>
          <div className="text-sm text-slate-500 dark:text-slate-400">{t('totalBlocked')}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
        <div className="flex flex-wrap gap-4 items-end">
          {/* Date Range */}
          <div className="flex gap-2 items-center">
            <div>
              <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1 flex items-center gap-1">
                {t('filters.start')}
                <HelpTip content={t('filters.startHelp')} />
              </label>
              <DatePicker
                selected={startDate}
                onChange={setStartDate}
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={30}
                dateFormat="MM/dd HH:mm"
                locale={dateLocale}
                className="px-3 py-1.5 border border-slate-300 dark:border-slate-600 rounded-lg text-sm w-36 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
              />
            </div>
            <span className="text-slate-400 mt-5">~</span>
            <div>
              <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1 flex items-center gap-1">
                {t('filters.end')}
                <HelpTip content={t('filters.endHelp')} />
              </label>
              <DatePicker
                selected={endDate}
                onChange={setEndDate}
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={30}
                dateFormat="MM/dd HH:mm"
                locale={dateLocale}
                className="px-3 py-1.5 border border-slate-300 dark:border-slate-600 rounded-lg text-sm w-36 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
              />
            </div>
          </div>

          {/* Rule Filter */}
          <div>
            <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1 flex items-center gap-1">
              {t('filters.rule')}
              <HelpTip content={t('filters.ruleHelp')} />
            </label>
            <select
              value={ruleFilter}
              onChange={e => { setRuleFilter(e.target.value); setPage(1); }}
              className="px-3 py-1.5 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 text-slate-900 dark:text-white min-w-[200px]"
            >
              <option value="">{t('filters.allRules')}</option>
              {ruleOptions.map(rule => (
                <option key={rule.id} value={rule.id}>
                  {rule.name}
                </option>
              ))}
            </select>
          </div>

          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1 flex items-center gap-1">
              {t('filters.search')}
              <HelpTip content={t('filters.searchHelp')} />
            </label>
            <input
              type="text"
              value={searchInput}
              onChange={e => { setSearchInput(e.target.value); setPage(1); }}
              placeholder={t('filters.searchPlaceholder')}
              className="w-full px-3 py-1.5 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
            />
          </div>

          {/* Per Page */}
          <div>
            <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1 flex items-center gap-1">
              {t('filters.perPage')}
              <HelpTip content={t('filters.perPageHelp')} />
            </label>
            <select
              value={perPage}
              onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}
              className="px-3 py-1.5 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-slate-500 dark:text-slate-400">{t('loading')}</div>
        ) : error ? (
          <div className="p-8 text-center text-red-500">{t('error')}</div>
        ) : !data?.data?.length ? (
          <div className="p-8 text-center text-slate-500 dark:text-slate-400">
            <div className="text-4xl mb-2">🎉</div>
            <div>{t('empty')}</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-red-50 dark:bg-red-900/20 border-b border-red-100 dark:border-red-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-red-700 dark:text-red-300">{t('table.time')}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-red-700 dark:text-red-300">{t('table.rule')}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-red-700 dark:text-red-300">{t('table.clientIp')}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-red-700 dark:text-red-300">{t('table.country')}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-red-700 dark:text-red-300">{t('table.host')}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-red-700 dark:text-red-300">{t('table.uri')}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-red-700 dark:text-red-300">{t('table.status')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {data.data.map((log) => (
                  <tr
                    key={log.id}
                    className="hover:bg-red-50 dark:hover:bg-slate-700/50 cursor-pointer transition-colors"
                    onClick={() => setSelectedLog(log)}
                  >
                    <td className="px-4 py-3 text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">
                      {formatTime(log.timestamp, i18n.language)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <ExploitRuleBadge ruleId={log.exploit_rule} ruleName={ruleNameMap[log.exploit_rule || '']} />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {log.is_banned ? (
                        <span className="flex items-center gap-1.5">
                          <span className="text-red-600 dark:text-red-400 font-mono text-sm">{log.client_ip}</span>
                          <span className="px-1.5 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs font-medium rounded">
                            Banned
                          </span>
                        </span>
                      ) : (
                        <span className="text-sm text-slate-600 dark:text-slate-300 font-mono">{log.client_ip || '-'}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300 whitespace-nowrap">
                      {log.geo_country_code || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300 whitespace-nowrap">
                      {log.host || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-red-700 dark:text-red-400 font-mono max-w-[250px] truncate" title={log.request_uri}>
                      {log.request_uri || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {log.status_code && <StatusCodeBadge code={log.status_code} />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {data && data.total_pages > 1 && (
          <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between bg-slate-50 dark:bg-slate-800">
            <div className="text-sm text-slate-500 dark:text-slate-400">
              {t('pageInfo', { page: data.page, total: data.total_pages, count: data.total })}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 border border-slate-300 dark:border-slate-600 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100 dark:hover:bg-slate-700 dark:text-slate-300"
              >
                {t('previous')}
              </button>
              <button
                onClick={() => setPage(p => Math.min(data.total_pages, p + 1))}
                disabled={page === data.total_pages}
                className="px-3 py-1 border border-slate-300 dark:border-slate-600 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100 dark:hover:bg-slate-700 dark:text-slate-300"
              >
                {t('next')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedLog && (
        <LogDetailModal
          log={selectedLog}
          ruleName={ruleNameMap[selectedLog.exploit_rule || '']}
          onClose={() => setSelectedLog(null)}
        />
      )}
    </div>
  );
}

export default ExploitBlockLogs;
